import React, { useMemo } from 'react';

import { motion } from 'framer-motion';

import {
  fadeInScaleVariants,
  fadeInVariants,
  slideInLeftVariants,
  slideInRightVariants,
  slideUpVariants,
  staggerContainerVariants,
  staggerItemVariants,
  viewportOnce,
} from '@/animations';

type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'scale' | 'none';

const variantsMap = {
  up: slideUpVariants,
  down: fadeInVariants,
  left: slideInLeftVariants,
  right: slideInRightVariants,
  scale: fadeInScaleVariants,
  none: fadeInVariants,
};

interface RevealProps {
  children: React.ReactNode;
  direction?: RevealDirection;
  delay?: number;
  duration?: number;
  className?: string;
  once?: boolean;
}

export const Reveal: React.FC<RevealProps> = React.memo(
  ({ children, direction = 'up', delay = 0, duration, className = '', once = true }) => {
    const variants = useMemo(() => variantsMap[direction], [direction]);
    const transition = useMemo(
      () => ({
        ...(duration ? { duration } : {}),
        delay,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      }),
      [delay, duration]
    );

    return (
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={once ? viewportOnce : undefined}
        variants={variants}
        transition={transition}
        className={className}>
        {children}
      </motion.div>
    );
  }
);
Reveal.displayName = 'Reveal';

interface StaggerRevealProps {
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
  animateOnMount?: boolean;
  once?: boolean;
}

export const StaggerReveal: React.FC<StaggerRevealProps> = React.memo(
  ({ children, className = '', staggerDelay = 0.1, animateOnMount = false, once = true }) => {
    const transition = useMemo(() => ({ staggerChildren: staggerDelay }), [staggerDelay]);

    return (
      <motion.div
        initial="initial"
        whileInView={!animateOnMount ? 'animate' : undefined}
        animate={animateOnMount ? 'animate' : undefined}
        viewport={!animateOnMount && once ? viewportOnce : undefined}
        variants={staggerContainerVariants}
        transition={transition}
        className={className}>
        {children}
      </motion.div>
    );
  }
);
StaggerReveal.displayName = 'StaggerReveal';

export const StaggerItem: React.FC<{ children: React.ReactNode; className?: string }> = React.memo(
  ({ children, className = '' }) => (
    <motion.div variants={staggerItemVariants} className={className}>
      {children}
    </motion.div>
  )
);
StaggerItem.displayName = 'StaggerItem';

export default Reveal;
